import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './sn-stats.css';

const DAY_PAGE = 14;

// Every column is sortable. `get` returns undefined for rows written before
// bep_daily_stats.py shipped (2026-05-13) — those carry no counters at all.
const COLS = [
  { key: 'day', label: 'Day (UTC)', num: false, get: r => r.startedAt.slice(0, 10) },
  { key: 'processed', label: 'Bugs processed', num: true, get: r => r.vendorData?.bugsProcessed },
  { key: 'created', label: 'Tickets created', num: true, get: r => r.vendorData?.ticketsCreated },
  { key: 'updated', label: 'Tickets updated', num: true, get: r => r.vendorData?.ticketsUpdated },
  { key: 'skipped', label: 'Skipped', num: true, get: r => r.vendorData?.skipped },
  { key: 'errors', label: 'Errors', num: true, get: r => r.vendorData?.errors },
  { key: 'runs', label: 'Delivery runs', num: true, get: r => r.vendorData?.runCount },
  // Rank by severity so a descending sort surfaces real failures above
  // not-yet-connected days, which are not failures at all.
  { key: 'status', label: 'Status', num: false, get: r => (!r.error ? 0 : r.errorKind === 'not_configured' ? 1 : 2) }
];

const Chev = () => (
  <svg
    className="sn-stats-chev"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

function sortRows(rows, sortKey, sortDir) {
  const col = COLS.find(c => c.key === sortKey);
  const dir = sortDir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = col.get(a);
    const bv = col.get(b);
    const aNull = av === undefined || av === null;
    const bNull = bv === undefined || bv === null;
    // Nulls last in BOTH directions, so legacy rows never crowd the top.
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (av === bv) return a.startedAt < b.startedAt ? 1 : -1;
    return (av > bv ? 1 : -1) * dir;
  });
}

const Cell = ({ value }) =>
  value === undefined || value === null ? (
    <td className="sn-stats-num" style={{ color: 'var(--ink-faint)' }}>
      &mdash;
    </td>
  ) : (
    <td className="sn-stats-num bz-tnum">{value.toLocaleString()}</td>
  );

Cell.propTypes = { value: PropTypes.number };

// A run that couldn't start because ServiceNow isn't connected yet is a setup
// state, not a fault — showing it in red tells the customer something is broken
// when nothing is. The endpoint classifies this for us via errorKind.
const StatusPill = ({ row }) => {
  if (!row.error) {
    return (
      <span className="bz-pill bz-pill--ok">
        <span className="bz-dot" />
        OK
      </span>
    );
  }
  if (row.errorKind === 'not_configured') {
    return (
      <span className="bz-pill bz-pill--warn" title="ServiceNow isn't connected yet">
        <span className="bz-dot" />
        Not connected
      </span>
    );
  }
  return (
    <span className="bz-pill bz-pill--bad" title={row.message || ''}>
      <span className="bz-dot" />
      Error
    </span>
  );
};

StatusPill.propTypes = { row: PropTypes.object.isRequired };

const DeliveriesTable = ({ rows }) => {
  const [sortKey, setSortKey] = useState('day');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(false);

  // Sort the FULL set, then page. Paging first would make "sort by Errors"
  // reorder only the visible 14 rows and hide the worst days in the window.
  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const shown = expanded ? sorted : sorted.slice(0, DAY_PAGE);

  const onSort = key => {
    if (key === sortKey) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (!rows.length) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--ink-muted)' }}>
        No bugs were delivered to ServiceNow in this window.
      </div>
    );
  }

  const col = COLS.find(c => c.key === sortKey);
  const direction =
    sortDir === 'desc' ? (col.num ? 'high to low' : 'newest first') : col.num ? 'low to high' : 'oldest first';

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table className="bz-table sn-stats-table">
          <thead>
            <tr>
              {COLS.map(c => (
                <th
                  key={c.key}
                  className={`sn-stats-sortable${c.num ? ' sn-stats-num' : ''}`}
                  aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button type="button" onClick={() => onSort(c.key)} data-testid={`sort-${c.key}`}>
                    {c.label}
                    <Chev />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.startedAt}>
                <td className="bz-mono">{r.startedAt.slice(0, 10)}</td>
                <Cell value={r.vendorData?.bugsProcessed} />
                <Cell value={r.vendorData?.ticketsCreated} />
                <Cell value={r.vendorData?.ticketsUpdated} />
                <Cell value={r.vendorData?.skipped} />
                <Cell value={r.vendorData?.errors} />
                <Cell value={r.vendorData?.runCount} />
                <td>
                  <StatusPill row={r} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--line-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10
        }}
      >
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-muted)' }}>
          {`Showing ${shown.length} of ${rows.length} days · sorted by ${col.label} (${direction}) across the whole window`}
        </span>
        {rows.length > DAY_PAGE && (
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </>
  );
};

DeliveriesTable.propTypes = {
  rows: PropTypes.array.isRequired
};

export default DeliveriesTable;
